import { Amplify as AmplifyCore } from "aws-amplify"
import { generateClient } from "aws-amplify/api"
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
  signOut as authSignOut,
  updateUserAttributes as authUpdateUserAttributes
} from "aws-amplify/auth"
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito"
import { Hub as HubCore } from "aws-amplify/utils"
import { Predictions as PredictionsCategory } from "@aws-amplify/predictions"

// Adapter used to preserve v5-style APIs while running on Amplify v6 modules.
// Use `let` so we can regenerate the client when auth headers change.
let graphqlClient = generateClient()
const isAmplifyApiDebugEnabled =
  import.meta.env?.VITE_DEBUG_AMPLIFY_API === "true"

const socialProviders = ["Google", "Facebook", "Amazon", "Apple"]

const toLegacyJwt = (jwtToken = "", payload: Record<string, any> = {}) => ({
  jwtToken,
  payload,
  getJwtToken: () => jwtToken
})

const toLegacySession = async (forceRefresh = false) => {
  const session: any = await fetchAuthSession({ forceRefresh })
  const idTokenJwt = session?.tokens?.idToken?.toString?.() || ""
  const accessTokenJwt = session?.tokens?.accessToken?.toString?.() || ""

  const idToken = toLegacyJwt(idTokenJwt, session?.tokens?.idToken?.payload || {})
  const accessToken = toLegacyJwt(
    accessTokenJwt,
    session?.tokens?.accessToken?.payload || {}
  )

  return {
    idToken,
    accessToken,
    refreshToken: null,
    getIdToken: () => idToken,
    getAccessToken: () => accessToken
  }
}

const normalizeNoCurrentUserError = () => {
  throw "No current user"
}

export const Amplify = {
  ...(AmplifyCore as any),
  configure: (config: any) => {
    const legacyHeaders = config?.API?.graphql_headers

    if (legacyHeaders) {
      if (!config.aws_appsync_graphqlEndpoint && !config.aws_project_region) {
        // v5 pattern: Amplify.configure({ API: { graphql_headers: fn } })
        // In v6, calling AmplifyCore.configure with a partial object completely
        // replaces the full config, wiping the AppSync endpoint and Cognito
        // settings and breaking every API call. Instead, regenerate the GraphQL
        // client with the header function using the v6-native headers option.
        graphqlClient = generateClient({ headers: legacyHeaders })
        return
      }
      // Full config that also carries graphql_headers — map to v6 format and configure.
      const normalizedConfig = {
        ...config,
        API: {
          ...(config?.API || {}),
          GraphQL: {
            ...(config?.API?.GraphQL || {}),
            headers: legacyHeaders
          }
        }
      }
      return AmplifyCore.configure(normalizedConfig)
    }

    return AmplifyCore.configure(config)
  },
  addPluggable: (pluggable: any) => {
    const amplifyAny = AmplifyCore as any
    if (typeof amplifyAny?.addPluggable === "function") {
      return amplifyAny.addPluggable(pluggable)
    }
    return undefined
  }
}

export const Auth = {
  configure: (config?: any) => {
    const storage = config?.storage
    if (storage) {
      cognitoUserPoolsTokenProvider.setKeyValueStorage(storage)
    }
    return config
  },
  currentSession: async (forceRefresh = false) => {
    try {
      const session = await toLegacySession(forceRefresh)
      if (!session?.idToken?.jwtToken && !session?.accessToken?.jwtToken) {
        normalizeNoCurrentUserError()
      }
      return session
    } catch (_error) {
      normalizeNoCurrentUserError()
    }
  },
  currentAuthenticatedUser: async () => {
    const [user, attributes, signInUserSession] = await Promise.all([
      getCurrentUser(),
      fetchUserAttributes().catch(() => ({})),
      toLegacySession().catch(() => null)
    ])

    return {
      ...(user as any),
      attributes,
      signInUserSession,
      refreshSession: async (_refreshToken: any, callback: any) => {
        try {
          const refreshedSession = await toLegacySession(true)
          callback?.(null, refreshedSession)
        } catch (error) {
          callback?.(error, null)
        }
      }
    }
  },
  updateUserAttributes: async (_user: any, attributes: Record<string, string>) =>
    authUpdateUserAttributes({ userAttributes: attributes }),
  federatedSignIn: async (options: any = {}) => {
    const requestedProvider = options?.provider || options?.customProvider
    const provider = requestedProvider
      ? socialProviders.includes(requestedProvider)
        ? requestedProvider
        : { custom: requestedProvider }
      : undefined

    return signInWithRedirect({
      provider,
      customState: options?.customState
    } as any)
  },
  signOut: async (options?: any) => authSignOut(options)
}

export const API = {
  // Generic type param accepted for v5 call-site compatibility.
  // Return type intentionally preserves promise/observable behavior expected by callers.
  graphql: <T = any>(params: any): T => {
    const request = params?.query ? { ...params } : { query: params }
    const authModeMap: Record<string, string> = {
      AMAZON_COGNITO_USER_POOLS: "userPool",
      AWS_IAM: "iam",
      API_KEY: "apiKey",
      OPENID_CONNECT: "oidc",
      AWS_LAMBDA: "lambda"
    }

    if (typeof request.authMode === "string" && authModeMap[request.authMode]) {
      request.authMode = authModeMap[request.authMode]
    }

    if (isAmplifyApiDebugEnabled) {
      const queryName =
        request?.query?.definitions?.[0]?.name?.value ||
        request?.query?.name?.value ||
        "unknown"
      console.log("[AmplifyAdapter] graphql request", {
        queryName,
        authMode: request?.authMode,
        hasRequestHeaders: Boolean(request?.headers)
      })
    }

    const result: any = graphqlClient.graphql(request as any)
    if (isAmplifyApiDebugEnabled && result && typeof result.then === "function") {
      return result.catch((error: any) => {
        console.error("[AmplifyAdapter] graphql error", {
          authMode: request?.authMode,
          errors: error?.errors,
          message: error?.message
        })
        throw error
      })
    }

    return result
  },
  // _message accepted for v5 call-site compatibility; unused in v6
  cancel: (operation: any, _message?: string) => {
    if (operation && typeof operation.unsubscribe === "function") {
      operation.unsubscribe()
      return true
    }
    // v6 graphql promise cancellation via AbortController is not supported;
    // existing code relying on this should migrate to AbortController directly.
    return false
  }
}

export const Hub = HubCore

export const Predictions = PredictionsCategory

export const graphqlOperation = (
  query: any,
  variables?: Record<string, any>,
  additionalHeaders?: Record<string, string>
) => ({
  query,
  variables,
  ...((additionalHeaders && { headers: additionalHeaders }) || {})
})
